/*
 * @Author: cjw 
 * @Date: 2021-03-23 16:47:22 
 * @Last Modified by: cjw
 * @Last Modified time: 2021-04-12 11:36:14
 */

/*
 * 提供主、从关系功能
 *
 * 提供以下接口
 * 1. current     获取、设置当前活动行对象
*/

import { DataTable } from '../data-table'
import { ProxyFilter } from './proxy-filter'

// 是否为数组的下标（索引）
function isArrayIndex(obj) {
    return /^\d+$/.test(obj)
}

function buildRelationColumnNames(meta) {
    // 检查主、从表是否有数据字典（必须有数据字典才可以建立关系）
    if (meta?.master?.columns?.length && meta?.detail?.columns?.length) {
        // 主从过滤
        if (meta?.master?.primaryKey?.length) {
            const relationColumnNames = []
            // 检查主表的主键字段在明细表中是否存在同名字段（有且最少有一个时认为可以建立关系）
            meta.master.primaryKey.forEach(colMaster => {
                const column = meta?.detail?.columns.find(colDetail => colDetail.field_name === colMaster.field_name)
                if (column) {
                    relationColumnNames.push(column.field_name)
                }
            })

            if (relationColumnNames) {
                // 保存一份，在明细表新增记录时用于填写外键
                return relationColumnNames
            } else {
                throw `创建数据源关系失败，明细表的字段需要至少有一个与主表的主键相同`
            }
        } else {
            throw `创建数据源关系失败，主表的数据字典未设置任何主键`
        }
    } else {
        return []
    }
}

function createReactiveDataTable(target, meta) {

    // 如果对象已有代理，则返回代理
    let observed = meta.toProxy.get(target)
    if (observed !== void 0) {
        return observed
    }

    if (meta.toRaw.get(target)) {
        return target
    }

    const createSetter = function(meta) {
        return function set(target, property, value, receiver) {

            // 新增时自动填写外键（仅当未提供外键时才填写，提供外键时认为这是有意要增加到另一明细上）
            if (isArrayIndex(property)) {
                if ((target.length - 1) < property) {
                    const parentRow = meta.master.current
                    if (parentRow) {
                        const foreignKey = {}
                        meta.relationColumnNames.forEach(columnName => {
                            const value = parentRow[columnName]
                            if (value) {
                                foreignKey[columnName] = parentRow[columnName]
                            } else {
                                throw `外键 ${columnName} 的值不能为空！`
                            }
                        })

                        value = Object.assign(foreignKey, value)
                    }
                }
            }
    
            // 设置属性值
            return Reflect.set(target, property, value, receiver)
        }
    }

    const handlers = {
        set: createSetter(meta)
    }

    // 创建 Proxy 代理
    observed = new Proxy(target, handlers)
    meta.toProxy.set(target, observed)
    meta.toRaw.set(observed, target)
    return observed
}

// 创建响应式对象
function createReactiveDataSource(target, meta) {

    const createGetter = function(meta) {
        return function get(target, property, receiver) {
            switch (property) {
                case 'datas': {
                    // 进行代理是为了解决删除行时改变当前活动行
                    const datas = Reflect.get(target, property, receiver)
                    return createReactiveDataTable(datas, meta)
                }
                default:
                    break;
            }
            
            return Reflect.get(target, property, receiver)
        }
    }

    const handlers = {
        get: createGetter(meta)
    }

    // 创建 Proxy 代理
    return new Proxy(target, handlers)
}

export function ProxyRelation(dtMaster, dtDetail, options) {
    const meta = {
        options: options,
        // 主实体
        master: dtMaster,
        // 明细实体
        detail: dtDetail,
        // 主从关系字段
        relationColumnNames: [],
        // 已代理的 DataRow 集合（key:原始对象，value:代理对象），用于解决重复代理
        toProxy: new WeakMap(),
        // 已代理的 DataRow 集合（key:代理对象，value:原始对象），与 toProxy 相反
        toRaw: new WeakMap()
    }

    meta.relationColumnNames = buildRelationColumnNames(meta)

    // 用 ProxyFilter 封装一层（用于实现主、从数据过滤，主实体必须使用 reactive 包一层，否则主表当前活动记录变化不会响应）
    const detailTable = new ProxyFilter(dtDetail)
    detailTable.filter(el => {
        if (!meta.relationColumnNames.length) {
            meta.relationColumnNames = buildRelationColumnNames(meta)
        }
        // 主表无记录时屏蔽所有明细记录
        let result = !!dtMaster.current && meta.relationColumnNames.length

        // 主表有当前记录时对数据进行过滤
        if (dtMaster.current) {
            for (let i = 0; i < meta.relationColumnNames.length; i++) {
                const relationColumnName = meta.relationColumnNames[i];
                if (el[relationColumnName] !== dtMaster.current[relationColumnName]) {
                    result = false
                    break;
                }
            }
        }
        
        return result
    })

    // 代理明细数据表，提供新增时自动填写外键
    const dataTable = createReactiveDataSource(detailTable, meta)
    
    // 重新用 DataTable 封装一层（解决对明细表过滤数据和当前行问题，否则会覆盖主从的过滤，当前行也会使用原 DataTable 的当前行）
    return new DataTable(dataTable)
}